import type { Role, User } from "../types";

const DEMO_SESSION_KEY = "hauffe-os-workspace-demo-session";

export interface DemoSession {
  userId: string;
  email: string;
  role: Role;
  name: string;
}

const demoUsers: Record<string, DemoSession> = {
  "torben@hauffe.io": {
    userId: "user-hauffe-admin",
    email: "torben@hauffe.io",
    role: "hauffe_admin",
    name: "Torben Hauffe"
  },
  "presse@hauffe.io": {
    userId: "user-company-owner",
    email: "presse@hauffe.io",
    role: "company_owner",
    name: "Presse Demo"
  },
  "torben.hauffe@gmail.com": {
    userId: "user-department-sales",
    email: "torben.hauffe@gmail.com",
    role: "department_contributor",
    name: "Abteilungsperspektive"
  }
};

export function getDemoUserByEmail(email: string): DemoSession | undefined {
  return demoUsers[email.trim().toLowerCase()];
}

export function getRoleStartPath(role: Role): string {
  if (role === "hauffe_admin") return "/admin";
  if (role === "company_owner") return "/workspace";
  return "/contributor";
}

export function getAssignedDepartmentId(session: DemoSession | undefined): string | undefined {
  if (!session || session.role !== "department_contributor") return undefined;
  if (session.email === "torben.hauffe@gmail.com") return "dept-sales";
  return undefined;
}

export function saveDemoSession(session: DemoSession): void {
  window.localStorage.setItem(DEMO_SESSION_KEY, JSON.stringify(session));
}

export function getDemoSession(): DemoSession | undefined {
  const raw = window.localStorage.getItem(DEMO_SESSION_KEY);
  if (!raw) return undefined;

  try {
    const parsed = JSON.parse(raw) as Partial<DemoSession>;
    if (!parsed.email || !parsed.role || !parsed.userId || !parsed.name) return undefined;
    return parsed as DemoSession;
  } catch {
    return undefined;
  }
}

export function clearDemoSession(): void {
  window.localStorage.removeItem(DEMO_SESSION_KEY);
}

export function userHasRole(session: DemoSession | undefined, allowedRoles: Role[]): session is DemoSession {
  return Boolean(session && allowedRoles.includes(session.role));
}

export function sessionToUser(session: DemoSession): User {
  const [firstName = "", ...lastNameParts] = session.name.split(" ");

  return {
    id: session.userId,
    email: session.email,
    firstName,
    lastName: lastNameParts.join(" "),
    role: session.role,
    organizationId: session.role === "hauffe_admin" ? undefined : "org-hauffe-demo"
  };
}
